/* סריקת דוח: קריאת הקובץ, סיווג, התאמה למה שכבר שמור, ושמירה */
import { touch } from '../domain/state.js';
import { mNorm, extractJSON, cleanNameGuard, noTags } from '../lib/text.js';
import { askAI, fileToBase64 } from '../lib/ai.js';
import { todayISO } from '../lib/format.js';
import { keyOfISO } from '../domain/periods.js';
import { cats } from '../domain/categories.js';
import { merchantMemory, applyMerchantMemory } from '../domain/merchants.js';
import { buildScanPrompt, buildClassifyPrompt } from '../domain/scan/prompts.js';
import { parseWorkbook } from '../domain/scan/excel.js';
import { scanReconcile, txExtraFields } from '../domain/scan/reconcile.js';
import { insertTx, addCategory, saveBalance, syncCommitsFromScan } from './actions.js';
import { sb, q } from '../lib/supabase.js';
import { toast } from './ui.js';

const IMG = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

/** סוג הקובץ לפי הסיומת */
export function fileKind(file) {
  const n = file.name.toLowerCase();
  if (n.endsWith('.xlsx') || n.endsWith('.xls')) return 'excel';
  if (n.endsWith('.csv')) return 'csv';
  if (file.type === 'application/pdf' || n.endsWith('.pdf')) return 'pdf';
  return 'image';
}

/**
 * @returns {Promise<{items:Array, meta:object}>}
 *   meta: { chargeDate, installments, deferred, settlements, bankLatest, detectedSrc, notes, mem }
 */
export async function analyzeFile(file, onProgress) {
  const kind = fileKind(file);
  if (kind === 'excel') {
    onProgress && onProgress('קורא קובץ...');
    const XLSX = await import('xlsx');
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array', cellDates: true, cellText: false });
    const res = parseWorkbook(XLSX, wb, file.name);
    res.notes.forEach((n) => toast(n));
    if (!res.rows.length) return analyzeText(res.csv, file.name, onProgress);
    onProgress && onProgress('חולצו ' + res.rows.length + ' שורות. מסווג בתי עסק...');
    const { items, mem } = await classifyRows(res.rows);
    return { items, meta: { ...res, mem } };
  }
  if (kind === 'csv') return analyzeText(await file.text(), file.name, onProgress);
  // תמונה או PDF: המודל קורא את המסמך ומחזיר את השורות
  let mediaType = file.type;
  if (kind === 'pdf') mediaType = 'application/pdf';
  else if (IMG.indexOf(mediaType) < 0) mediaType = 'image/jpeg';
  const data = await fileToBase64(file);
  if (data.length > 18 * 1024 * 1024) throw new Error('הקובץ גדול מדי לסריקה. נסה לדחוס או לצלם בחלקים');
  onProgress && onProgress('מנתח את המסמך... (עד דקה)');
  const text = await askAI('scan', buildScanPrompt('all'), { data, mediaType });
  return aiItems(text);
}

/** גיליון בפורמט לא מוכר או CSV: נשלח כטקסט למודל */
async function analyzeText(text, fileName, onProgress) {
  text = String(text || '').trim();
  if (!text) throw new Error('הקובץ ריק');
  onProgress && onProgress('מנתח את הקובץ... (עד דקה)');
  const out = await askAI('scan', buildScanPrompt('all') + '\n== תוכן הקובץ ' + (fileName || '') + ' ==\n' + text.slice(0, 60000));
  return aiItems(out);
}

async function aiItems(text) {
  let items = extractJSON(text) || [];
  items = items.filter((i) => i && Number(i.amount) > 0).map((i) => ({
    description: noTags(i.description || 'ללא תיאור'), amount: Math.abs(Number(i.amount)), type: i.type === 'income' ? 'income' : 'expense',
    cat: i.cat || 'other', date: i.date || todayISO(), internal: i.internal === true,
    ref: i.ref || '', card4: i.card4 || '', raw: noTags(i.raw || ''), orig: i.orig || '', isNewCat: i.isNewCat, newCatIcon: i.newCatIcon,
  }));
  if (!items.length) throw new Error('לא זוהו עסקאות במסמך');
  await createNewCats(items.filter((i) => i.isNewCat).map((i) => ({ id: i.cat, icon: i.newCatIcon })));
  const mem = applyMerchantMemory(items);
  return { items, meta: { notes: [], mem } };
}

/** קטגוריה חדשה שהמודל הציע נשמרת, כדי שלא תיעלם בטעינה הבאה */
async function createNewCats(list) {
  for (const c of list) {
    if (!c.id || cats('expense').some((x) => x.id === c.id)) continue;
    try { await addCategory({ id: c.id, label: c.label || c.id, icon: c.icon || '📦', color: '#888888', type: 'expense' }); } catch (e) { console.error(e); }
  }
}

/** סיווג שורות שה-JS כבר חילץ: מהזיכרון אם מוכר, אחרת שמות בלבד למודל. הסכומים לא עוברים במודל */
async function classifyRows(recs) {
  const mem = merchantMemory();
  const known = {}, unknown = [], seen = new Set();
  recs.forEach((r) => {
    if (seen.has(r.name)) return;
    seen.add(r.name);
    const k = mNorm(r.name), m = k.length >= 3 ? mem[k] : null;
    if (m && m.cat && m.agree >= 0.5) known[r.name] = { cat: m.cat, type: m.type || 'expense', fromMemory: true };
    else unknown.push(r.name);
  });
  if (unknown.length) {
    try {
      const arr = extractJSON(await askAI('classify', buildClassifyPrompt(unknown))) || [];
      const newCats = [];
      arr.forEach((o) => {
        if (!o || !o.name) return;
        if (o.isNewCat && o.cat) newCats.push({ id: o.cat, label: o.clean || o.cat, icon: o.newCatIcon });
        known[o.name] = { cat: o.cat || 'other', type: o.type || 'expense', clean: o.clean, internal: o.internal === true };
      });
      await createNewCats(newCats);
      const missing = unknown.filter((n) => !known[n]).length;
      if (missing) toast('⚠️ ' + missing + ' שמות לא סווגו, סומנו כאחר');
    } catch (e) {
      toast('סיווג נכשל (' + e.message + '). כל השורות נשמרות כאחר');
    }
  }
  const items = recs.map((r) => {
    const c = known[r.name] || { cat: 'other', type: 'expense' };
    const name = noTags(r.name);
    return {
      // ניקוי לגיטימי רק משמיט מילים. מילה עברית חדשה פירושה שהמודל הפך או שיבש
      description: cleanNameGuard(name, c.clean && noTags(c.clean)) || name,
      amount: r.amount, type: r.type || c.type || 'expense', cat: c.cat || 'other', date: r.date,
      internal: c.internal === true || r.cardSettlement === true, cardSettlement: r.cardSettlement === true,
      notYetBilled: r.notYetBilled === true, billMonth: r.billMonth, fxRate: r.fxRate, fxSrc: r.fxSrc, standing: r.standing === true,
      fromMemory: c.fromMemory === true, instNum: r.instNum, instTotal: r.instTotal,
      ref: r.ref || '', card4: r.card4 || '', orig: r.orig || '', raw: noTags(r.raw || r.name),
    };
  });
  return { items, mem: applyMerchantMemory(items) };
}

/** התקופה שאליה תישמר שורה */
export function itemMonth(it, selKey, chargeDate) {
  const d = it.date || todayISO();
  let m = it.month || selKey || keyOfISO(d);
  // רכישה שטרם נקלטה ובוצעה אחרי יום החיוב שייכת לחיוב הבא, לא לדוח הזה
  if (!it.month && selKey && it.notYetBilled && it.billMonth && chargeDate && selKey === keyOfISO(chargeDate) && it.billMonth > chargeDate) m = keyOfISO(it.billMonth);
  return m;
}

export function previewPlan(items, src, selKey) {
  const valid = [], idx = [];
  // שורה שהמשתמש סימן "לא לשמור" לא נכנסת להתאמה בכלל
  items.forEach((it, i) => { if (it.amount > 0 && !it.skip) { valid.push(it); idx.push(i); } });
  const plan = scanReconcile(valid, src, todayISO(), selKey);
  const st = items.map(() => ({ status: 'skip' }));
  idx.forEach((orig, k) => (st[orig] = plan.res[k]));
  return { st, missing: plan.missing, older: plan.older };
}

/** שמירה: חדשות נשמרות, קיימות מקבלות השלמת מזהים, "לבדיקה" חוזרות לאישור */
export async function saveScan(items, src, selKey, meta) {
  const today = todayISO();
  const valid = items.filter((it) => it.amount > 0 && !it.skip);
  const plan = scanReconcile(valid, src, today, selKey);
  let saved = 0, matched = 0, updated = 0, infile = 0, failed = 0, firstErr = '';
  const near = [];
  for (let i = 0; i < valid.length; i++) {
    const it = valid[i], pr = plan.res[i];
    const date = it.date || today, month = itemMonth(it, selKey, meta.chargeDate);
    if (pr.status === 'infile') { infile++; continue; }
    if (pr.status === 'dup') {
      matched++;
      // משלימים מזהים לשורה הישנה, כדי שבפעם הבאה ההתאמה תהיה לפי אסמכתא
      const ex = pr.existing, patch = {}, fx = txExtraFields(it);
      if (fx.ref && !ex.ref) patch.ref = fx.ref;
      if (fx.raw_name && !ex.raw_name) patch.raw_name = fx.raw_name;
      if (fx.card4 && !ex.card4) patch.card4 = fx.card4;
      if (fx.orig_amount && !ex.orig_amount) patch.orig_amount = fx.orig_amount;
      if (pr.updateAmount) { patch.amount = it.amount; updated++; }
      if ((it.internal || it.cardSettlement) && ex.excluded !== true) patch.excluded = true;
      if (pr.updateDate) { patch.date = date; patch.month = month; }
      // השם הסופי מחליף את שם חברת הסליקה, אלא אם התיאור שונה ידנית
      if (pr.rename && it.raw) {
        if (mNorm(ex.description) === mNorm(ex.raw_name)) patch.description = it.description || it.raw;
        patch.raw_name = String(it.raw).trim().slice(0, 200);
      }
      if (Object.keys(patch).length) {
        Object.assign(ex, patch);
        q(sb().from('transactions').update(patch).eq('id', ex.id)).catch(() => {});
      }
      continue;
    }
    if (pr.status === 'near') { near.push({ item: { ...it, month }, existing: pr.existing }); continue; }
    const body = { description: it.description || 'פריט ' + (i + 1), amount: it.amount, type: it.type || 'expense', cat: it.cat || 'other', date, month, source: src };
    // שורה פנימית (חיוב הכרטיס בעו"ש, העברה בין חשבונות): לא נספרת כהוצאה, אבל נשארת ביתרת העו"ש
    if (it.internal || it.cardSettlement) body.excluded = true;
    try { await insertTx(body, it); saved++; } catch (e) { failed++; if (!firstErr) firstErr = e.message; console.error(e); }
  }
  touch();
  await syncCommitsFromScan(valid, meta);
  return { saved, matched, updated, infile, failed, firstErr, near, missing: plan.missing };
}

export function applyBankBalance(bl) {
  saveBalance(bl.balance, bl.date, true);
  toast('✅ נקודת הפתיחה עודכנה');
}

